import { Client, type Room } from "@colyseus/sdk";
import type { ClassId, Difficulty, Mode } from "@eldrun/content";
import { MATCH_ROOM, MSG, normalizeRoomCode, type ConfigureMessage, type JoinOptions, type MatchStateType, type Notice, type UnlockedEvent, type PingEvent } from "@eldrun/protocol";

export type MatchRoomHandle = Room<unknown, MatchStateType>;

export interface PlayOptions {
  name: string;
  mode: Mode;
  difficulty: Difficulty;
  /** JWT da conta (F34); sem ele, convidado. */
  token?: string;
  /** Classe escolhida no menu (F64); sem ela, a sugerida pelo slot. */
  classId?: ClassId;
  /** Seed fixa da sala privada (a revanche; em desenvolvimento, o `?semente=` do "Jogar sozinho"). */
  seed?: string;
}

/**
 * Sessão numa sala de partida: envia intenções validadas pelos esquemas de `@eldrun/protocol` e
 * repassa estado e avisos do servidor. Toda regra é decidida no servidor.
 */
export class MatchSession {
  readonly room: MatchRoomHandle;
  /** Receitas liberadas antes de a cena existir (ex.: no nascimento) esperam aqui. */
  private readonly pendingUnlocks: UnlockedEvent[] = [];
  private unlockListener: ((event: UnlockedEvent) => void) | undefined;
  /** RTT que o servidor mediu desta conexão (ms), do último ping. */
  serverRtt = 0;

  constructor(room: MatchRoomHandle) {
    this.room = room;
    // Latência medida pelo servidor: devolve o número do ping na hora.
    room.onMessage(MSG.ping, (event: PingEvent) => {
      room.send(MSG.pong, { n: event.n });
      this.serverRtt = event.rtt;
    });
    room.onMessage(MSG.unlocked, (event: UnlockedEvent) => {
      if (this.unlockListener) this.unlockListener(event);
      else this.pendingUnlocks.push(event);
    });
  }

  get sessionId(): string {
    return this.room.sessionId;
  }

  get state(): MatchStateType {
    return this.room.state;
  }

  /** Classe no lobby (F64). */
  chooseClass(classId: ClassId): void {
    this.room.send(MSG.chooseClass, { classId });
  }

  setReady(ready: boolean): void {
    this.room.send(MSG.ready, { ready });
  }

  configure(message: ConfigureMessage): void {
    this.room.send(MSG.configure, message);
  }

  start(): void {
    this.room.send(MSG.start, {});
  }

  kick(sessionId: string): void {
    this.room.send(MSG.kick, { sessionId });
  }

  reportLoaded(worldHash: number, resourceHash: number): void {
    this.room.send(MSG.loaded, { worldHash: worldHash >>> 0, resourceHash: resourceHash >>> 0 });
  }

  harvest(resourceId: number): void {
    this.room.send(MSG.harvest, { resourceId });
  }

  onState(callback: (state: MatchStateType) => void): () => void {
    const handler = (state: MatchStateType) => callback(state);
    this.room.onStateChange(handler);
    return () => this.room.onStateChange.remove(handler);
  }

  onNotice(callback: (notice: Notice) => void): () => void {
    return this.room.onMessage(MSG.notice, (notice: Notice) => callback(notice));
  }

  /** Avisos de receitas novas; entrega na hora os que chegaram antes do ouvinte. */
  onUnlocked(callback: (event: UnlockedEvent) => void): () => void {
    this.unlockListener = callback;
    for (const event of this.pendingUnlocks.splice(0)) callback(event);
    return () => {
      if (this.unlockListener === callback) this.unlockListener = undefined;
    };
  }

  onLeave(callback: (code: number) => void): void {
    this.room.onLeave(callback);
  }

  leave(): Promise<number> {
    return this.room.leave(true);
  }
}

/** Matchmaking: fila pública por modo/dificuldade, sala privada e entrada por código. */
export class MatchClient {
  private readonly client: Client;

  constructor(serverUrl: string) {
    this.client = new Client(serverUrl);
  }

  async quickPlay(options: PlayOptions): Promise<MatchSession> {
    const join: JoinOptions = { ...options, private: false };
    return new MatchSession(await this.client.joinOrCreate<MatchStateType>(MATCH_ROOM, join));
  }

  /** Sala privada sempre por `create`: `joinOrCreate` poderia cair numa pública. */
  async createPrivate(options: PlayOptions): Promise<MatchSession> {
    const join: JoinOptions = { ...options, private: true };
    return new MatchSession(await this.client.create<MatchStateType>(MATCH_ROOM, join));
  }

  /** "Jogar sozinho" (F54): sala privada da Irmandade, vaga única, que o servidor já põe para carregar. */
  async playSolo(options: PlayOptions): Promise<MatchSession> {
    const join: JoinOptions = { ...options, mode: "mutirao", private: true, solo: true };
    return new MatchSession(await this.client.create<MatchStateType>(MATCH_ROOM, join));
  }

  async joinByCode(code: string, name: string, token?: string, classId?: ClassId): Promise<MatchSession> {
    const join: Partial<JoinOptions> = { name, ...(token ? { token } : {}), ...(classId ? { classId } : {}) };
    return new MatchSession(await this.client.joinById<MatchStateType>(normalizeRoomCode(code), join));
  }
}
